import { draftingTools } from "./drafting-tools";
import { TracePlacementTool } from "./TracePlacementTool";
import { snapEndpoints } from "./snap-settings";
import { unitsPerMeter } from "../shared/mark-presentation";
import { drawingDefaults } from "./drawing-defaults";
import { activeTrace } from "./tracing";
import { sourceAt, sourceMeters, type DrawingSource } from "./source-scale";
import { StateNode } from "./canvas";
import { createShapeId, type TLShapeId, type TLShape } from "@tldraw/tlschema";
import type { Editor } from "./canvas";
import type { Sheet, Point, Mark } from "../shared/schema";
import { distance } from "../shared/geometry";
import { notify } from "./api";
import { getLineAlignment, type LineAxis } from "./line-alignment";
import { getLineGuide } from "./line-guide-state";
import {
  clampCommentPoint,
  commentRegionForStroke,
  commentStrokeLength,
  type CommentRegion,
} from "./comment-regions";
export const toolSettings = { color: "#d65331", length: 0 };
export const controllers = new WeakMap<
  Editor,
  {
    progress: (points: number, start?: Point) => void;
    calibrate: (sheet: DrawingSource, a: Point, b: Point) => void;
    requestScale: (sheet: DrawingSource, tool: "dimension" | "area") => void;
    comment: (shape: TLShape | null, p: Point) => void;
    previewCommentArea: (
      target: TLShape | null,
      region: CommentRegion | null,
      stroke: Point[] | null,
    ) => void;
    commentArea: (
      target: TLShape,
      region: CommentRegion,
      stroke: Point[],
    ) => void;
    /** Cmd/Ctrl+K: a new @Agent comment pinned to the selection. */
    askAgent: () => void;
  }
>();
function findSheet(editor: Editor, p: Point) {
  return editor
    .getCurrentPageShapesSorted()
    .filter((s): s is Sheet => s.type === "sheet")
    .reverse()
    .find((s) => {
      const q = editor.getPointInShapeSpace(s, p);
      return q.x >= 0 && q.y >= 0 && q.x <= s.props.w && q.y <= s.props.h;
    });
}
export class PrecisionTool extends StateNode {
  static override id = "dimension";
  sheet?: DrawingSource;
  markId?: TLShapeId;
  points: Point[] = [];
  history?: string;
  get lineGuide() {
    return getLineGuide(this.editor);
  }
  override onEnter() {
    this.editor.setCursor({ type: "cross", rotation: 0 });
    this.points = [];
    this.lineGuide.set(null);
  }
  kind() {
    return this.getPath().split(".")[1] || this.getPath();
  }
  localPoint() {
    const raw = this.editor.inputs.getCurrentPagePoint(),
      // The first point fixes the coordinate space, including the bare canvas.
      sheet = this.points.length
        ? this.sheet
        : (["line", "polygon"].includes(this.kind())
            ? activeTrace(this.editor)
            : undefined) || sourceAt(this.editor, raw);
    if (!sheet && this.kind() === "calibrate") return undefined;
    const parentId = sheet?.id ?? this.editor.getCurrentPageId();
    let p = sheet ? this.editor.getPointInShapeSpace(sheet, raw) : raw,
      last = this.points[this.points.length - 1];
    const near = this.editor
      .getCurrentPageShapes()
      .filter(
        (s): s is Mark =>
          s.type === "mark" && s.parentId === parentId && s.id !== this.markId,
      )
      .flatMap((s) =>
        s.props.points.map((q) => ({ x: s.x + q.x, y: s.y + q.y })),
      )
      .find((q) => distance(q, p) < 8 / this.editor.getZoomLevel());
    if (near && snapEndpoints(this.editor) && !this.editor.inputs.altKey)
      p = { ...p, ...near } as typeof p;
    let lineAxis: LineAxis | null = null;
    if (
      last &&
      this.kind() === "line" &&
      !(near && snapEndpoints(this.editor)) &&
      !this.editor.inputs.altKey &&
      !this.editor.inputs.shiftKey
    ) {
      const alignment = getLineAlignment(
        last,
        p,
        this.editor.getZoomLevel(),
        this.lineGuide.get()?.axis,
      );
      p = { ...p, ...alignment.point } as typeof p;
      lineAxis = alignment.axis;
    }
    if (last && this.editor.inputs.shiftKey) {
      const angle =
        (Math.round(Math.atan2(p.y - last.y, p.x - last.x) / (Math.PI / 4)) *
          Math.PI) /
        4;
      const radius = distance(last, p);
      p = {
        ...p,
        x: last.x + Math.cos(angle) * radius,
        y: last.y + Math.sin(angle) * radius,
      } as typeof p;
      if (this.kind() === "line" && !this.editor.inputs.altKey)
        lineAxis = getLineAlignment(last, p, this.editor.getZoomLevel()).axis;
    }
    if (
      last &&
      toolSettings.length > 0 &&
      sourceMeters(this.editor, sheet) > 0 &&
      ["line", "dimension"].includes(this.kind())
    ) {
      const d = distance(last, p) || 1,
        l =
          toolSettings.length /
          unitsPerMeter[drawingDefaults(this.editor, this.kind()).unit] /
          sourceMeters(this.editor, sheet);
      p = {
        ...p,
        x: last.x + ((p.x - last.x) / d) * l,
        y: last.y + ((p.y - last.y) / d) * l,
      } as typeof p;
    }
    if (last && lineAxis) {
      const transform = sheet && this.editor.getShapePageTransform(sheet);
      this.lineGuide.set({
        start: transform ? transform.applyToPoint(last) : last,
        end: transform ? transform.applyToPoint(p) : p,
        axis: lineAxis,
        locked: this.editor.inputs.shiftKey,
      });
    } else {
      this.lineGuide.set(null);
    }
    return { sheet, p: { x: p.x, y: p.y } };
  }
  override onPointerDown() {
    if (this.kind() === "pin") {
      const point = this.editor.inputs.getCurrentPagePoint(),
        shape = this.editor.getShapeAtPoint(point, { hitInside: true });
      const p = shape ? this.editor.getPointInShapeSpace(shape, point) : point;
      controllers.get(this.editor)?.comment(shape ?? null, p);
      this.editor.setCurrentTool("select");
      return;
    }
    let hit;
    try {
      hit = this.localPoint();
    } catch (e) {
      notify((e as Error).message);
      return;
    }
    if (!hit) {
      notify("Click inside a sheet or source image to set its drawing scale.");
      return;
    }
    if (
      ["dimension", "area"].includes(this.kind()) &&
      hit.sheet &&
      !sourceMeters(this.editor, hit.sheet)
    ) {
      controllers.get(this.editor)?.requestScale(
        hit.sheet,
        this.kind() as "dimension" | "area",
      );
      return;
    }
    this.sheet = hit.sheet;
    this.editor.setHintingShapes(hit.sheet ? [hit.sheet.id] : []);
    if (!this.points.length)
      this.history = this.editor.markHistoryStoppingPoint(undefined, { hold: true });
    if (
      ["area", "polygon"].includes(this.kind()) &&
      this.points.length >= 3 &&
      distance(hit.p, this.points[0]) < 8 / this.editor.getZoomLevel()
    ) {
      this.finish();
      return;
    }
    this.points.push(hit.p);
    controllers
      .get(this.editor)
      ?.progress(
        this.points.length,
        this.sheet
          ? this.editor
              .getShapePageTransform(this.sheet)!
              .applyToPoint(this.points[0])
          : this.points[0],
      );
    if (this.kind() === "calibrate" && this.sheet) {
      if (this.points.length === 2) {
        controllers
          .get(this.editor)
          ?.calibrate(this.sheet, this.points[0], this.points[1]);
        this.points = [];
        this.sheet = undefined;
        this.editor.setCurrentTool("select");
      }
      return;
    }
    if (!this.markId) {
      this.markId = createShapeId();
      const style = drawingDefaults(this.editor, this.kind());
      this.editor.createShape<Mark>({
        id: this.markId,
        type: "mark",
        parentId: this.sheet?.id ?? this.editor.getCurrentPageId(),
        x: 0,
        y: 0,
        meta: {
          ...style,
          ...(this.kind() === "polygon" ? { polygon: true } : {}),
        },
        opacity: style.opacity,
        props: {
          kind: (this.kind() === "polygon"
            ? "area"
            : this.kind()) as Mark["props"]["kind"],
          points: [hit.p, { x: hit.p.x + 0.01, y: hit.p.y + 0.01 }],
          color: style.color,
          weight: style.weight,
        },
      });
    }
    if (
      (["line", "dimension"].includes(this.kind()) &&
        this.points.length === 2) ||
      (this.kind() === "angle" && this.points.length === 3)
    ) {
      this.finish();
      return;
    }
    this.update(hit.p);
  }
  update(p: Point) {
    if (this.markId)
      this.editor.updateShape<Mark>({
        id: this.markId,
        type: "mark",
        props: { points: [...this.points, p] },
      });
  }
  override onPointerMove() {
    let hit;
    try {
      hit = this.localPoint();
    } catch (e) {
      notify((e as Error).message);
      return;
    }
    if (hit && this.points.length) this.update(hit.p);
  }
  finish() {
    if (this.markId && this.points.length >= 2) {
      this.editor.updateShape<Mark>({
        id: this.markId,
        type: "mark",
        props: { points: this.points },
      });
      this.editor.select(this.markId);
    }
    this.markId = undefined;
    this.points = [];
    this.sheet = undefined;
    this.editor.setCurrentTool("select");
  }
  override onDoubleClick() {
    if (["area", "polygon"].includes(this.kind()) && this.points.length >= 3) {
      if (
        distance(
          this.points[this.points.length - 1],
          this.points[this.points.length - 2],
        ) < 1
      )
        this.points.pop();
      this.finish();
    }
  }
  undoCorner() {
    if (!this.points.length || !["area", "polygon"].includes(this.kind()))
      return;
    this.points.pop();
    if (!this.points.length) {
      this.cancel();
      return;
    }
    this.update(this.points[this.points.length - 1]);
    controllers.get(this.editor)?.progress(this.points.length);
  }
  override onKeyDown(info: { key: string }) {
    if (info.key === "Backspace") this.undoCorner();
    if (
      info.key === "Enter" &&
      ["area", "polygon"].includes(this.kind()) &&
      this.points.length >= 3
    )
      this.finish();
    if (info.key === "Escape") this.cancel();
    if (info.key === "Alt" || info.key === "Shift") this.onPointerMove();
  }
  override onKeyUp(info: { key: string }) {
    if (info.key === "Alt" || info.key === "Shift") this.onPointerMove();
  }
  cancel() {
    if (this.markId && this.history) this.editor.bailToMark(this.history);
    this.markId = undefined;
    this.points = [];
    this.sheet = undefined;
    this.editor.setCurrentTool("select");
  }
  override onCancel() {
    this.cancel();
  }
  override onExit() {
    this.lineGuide.set(null);
    this.editor.setHintingShapes([]);
    controllers.get(this.editor)?.progress(0);
    if (this.markId && this.history) this.editor.bailToMark(this.history);
    this.markId = undefined;
    this.points = [];
    this.sheet = undefined;
  }
}
export class LineTool extends PrecisionTool {
  static override id = "line";
}
export class AreaTool extends PrecisionTool {
  static override id = "area";
}
export class PolygonTool extends PrecisionTool {
  static override id = "polygon";
}
export class AngleTool extends PrecisionTool {
  static override id = "angle";
}
export class CalibrationTool extends PrecisionTool {
  static override id = "calibrate";
}
export class PinTool extends PrecisionTool {
  static override id = "pin";
}
export class CommentAreaTool extends StateNode {
  static override id = "comment-area";
  target?: TLShape;
  points: Point[] = [];

  override onEnter() {
    this.editor.setCursor({ type: "cross", rotation: 0 });
    this.clear();
  }

  localPoint(): Point | undefined {
    if (!this.target) return;
    return clampCommentPoint(
      this.editor.getPointInShapeSpace(
        this.target,
        this.editor.inputs.getCurrentPagePoint(),
      ),
      this.editor.getShapeGeometry(this.target).bounds,
    );
  }

  updatePreview() {
    const point = this.localPoint();
    if (!this.target || !point) return;
    const zoom = this.editor.getZoomLevel();
    const last = this.points[this.points.length - 1];
    if (!last) this.points = [point];
    else if (Math.hypot(point.x - last.x, point.y - last.y) >= 2 / zoom) {
      if (this.points.length < 240) this.points = [...this.points, point];
      else this.points = [...this.points.slice(0, -1), point];
    }
    const region = commentRegionForStroke(
      this.points,
      10 / zoom,
      this.editor.getShapeGeometry(this.target).bounds,
    );
    controllers
      .get(this.editor)
      ?.previewCommentArea(this.target, region, this.points);
  }

  override onPointerDown() {
    const point = this.editor.inputs.getCurrentPagePoint();
    const target =
      findSheet(this.editor, point) ||
      this.editor.getShapeAtPoint(point, { hitInside: true });
    if (!target) {
      notify("Drag over a drawing, image or object to mark an area.");
      return;
    }
    this.target = target;
    this.points = [
      clampCommentPoint(
        this.editor.getPointInShapeSpace(target, point),
        this.editor.getShapeGeometry(target).bounds,
      ),
    ];
    this.editor.setHintingShapes([target.id]);
    this.updatePreview();
  }

  override onPointerMove() {
    this.updatePreview();
  }

  override onPointerUp() {
    if (!this.target) return;
    this.updatePreview();
    const zoom = this.editor.getZoomLevel();
    if (commentStrokeLength(this.points) < 8 / zoom) {
      notify("Sweep across an area before adding the comment.");
      this.clear();
      return;
    }
    const region = commentRegionForStroke(
      this.points,
      10 / zoom,
      this.editor.getShapeGeometry(this.target).bounds,
    );
    controllers.get(this.editor)?.commentArea(this.target, region, this.points);
    this.target = undefined;
    this.points = [];
    this.editor.setCurrentTool("select");
  }

  clear() {
    controllers.get(this.editor)?.previewCommentArea(null, null, null);
    this.editor.setHintingShapes([]);
    this.target = undefined;
    this.points = [];
  }

  override onCancel() {
    this.clear();
    this.editor.setCurrentTool("select");
  }

  override onExit() {
    this.clear();
  }
}
export const customTools = [
  ...draftingTools,
  TracePlacementTool,
  PrecisionTool,
  LineTool,
  AreaTool,
  PolygonTool,
  AngleTool,
  CalibrationTool,
  PinTool,
  CommentAreaTool,
];
